import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { inviteAPI } from '../services/api';
import ReCAPTCHA from 'react-google-recaptcha';
import './Auth.css';

const RECAPTCHA_SITE_KEY = '6Ld0ImMsAAAAAHqPz5q3NlfJhlRVjBrez7cxKuPV';

const passwordRules = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'One number', test: (p) => /[0-9]/.test(p) },
  { label: 'One special character (!@#$%...)', test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(p) },
];

const Register = () => {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // If arriving from invite link, store token so it survives registration
  useEffect(() => {
    const inviteToken = searchParams.get('invite');
    if (inviteToken) {
      localStorage.setItem('pendingInvite', inviteToken);
    }
  }, []);

  const ruleResults = useMemo(() => 
    passwordRules.map(rule => ({ ...rule, passed: rule.test(password) })),
    [password]
  );

  const allRulesPassed = ruleResults.every(r => r.passed);
  const passedCount = ruleResults.filter(r => r.passed).length;

  const strengthLabel = passedCount === 0 ? '' : passedCount <= 1 ? 'Weak' : passedCount <= 2 ? 'Fair' : passedCount <= 3 ? 'Good' : 'Strong';
  const strengthColor = passedCount <= 1 ? '#ef4444' : passedCount <= 2 ? '#f59e0b' : passedCount <= 3 ? '#3b82f6' : '#22c55e';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!allRulesPassed) {
      setError('Please meet all password requirements');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!agreedToTerms) {
      setError('Please agree to the Terms of Service, Privacy Policy, and EULA');
      return;
    }

    if (!captchaToken) {
      setError('Please complete the CAPTCHA verification');
      return;
    }

    setLoading(true);

    try {
      await register(displayName, email, password, captchaToken);

      const pendingInvite = localStorage.getItem('pendingInvite');
      if (pendingInvite) {
        try {
          const res = await inviteAPI.accept(pendingInvite);
          localStorage.removeItem('pendingInvite');
          navigate(`/family/${res.data.familyCircle._id}`);
          return;
        } catch (inviteErr) {
          console.error('Failed to accept invite:', inviteErr);
          localStorage.removeItem('pendingInvite');
        }
      }

      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
      if (captchaRef.current) {
        captchaRef.current.reset();
        setCaptchaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="auth-orb auth-orb-1"></div>
        <div className="auth-orb auth-orb-2"></div>
        <div className="auth-orb auth-orb-3"></div>
      </div>

      <div className="auth-card">
        <div className="auth-logo">
          <img src="/logo.svg" alt="EchoMemoir" style={{ width: '80px', height: '80px' }} />
          <h1>EchoMemoir</h1>
          <p>Preserve voices and stories for generations</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <h2>Create Account</h2>

          {error && <div className="auth-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="displayName">Your Name</label>
            <input
              type="text"
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="John Smith"
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a strong password"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>

            {password.length > 0 && (
              <div className="password-strength">
                <div className="strength-bar">
                  <div
                    className="strength-fill"
                    style={{
                      width: `${(passedCount / passwordRules.length) * 100}%`,
                      backgroundColor: strengthColor
                    }}
                  ></div>
                </div>
                <span className="strength-label" style={{ color: strengthColor }}>
                  {strengthLabel}
                </span>
              </div>
            )}

            {password.length > 0 && (
              <ul className="password-rules">
                {ruleResults.map((rule, i) => (
                  <li key={i} className={rule.passed ? 'passed' : 'failed'}>
                    <span className="rule-icon">{rule.passed ? '✔' : '○'}</span>
                    {rule.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex={-1}
              >
                {showConfirmPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {confirmPassword.length > 0 && password !== confirmPassword && (
              <p className="field-error">Passwords do not match</p>
            )}
            {confirmPassword.length > 0 && password === confirmPassword && (
              <p className="field-success">Passwords match ✔</p>
            )}
          </div>

          <div className="legal-agree">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
              <span>
                I agree to the <Link to="/terms" target="_blank">Terms of Service</Link>,{' '}
                <Link to="/privacy" target="_blank">Privacy Policy</Link>, and{' '}
                <Link to="/eula" target="_blank">End User License Agreement</Link>
              </span>
            </label>
          </div>

          <div className="captcha-wrapper">
            <ReCAPTCHA
              ref={captchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={(token) => setCaptchaToken(token)}
              onExpired={() => setCaptchaToken(null)}
              theme="dark"
            />
          </div>

          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? <span className="spinner"></span> : 'Create Account'}
          </button>

          <p className="auth-switch">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Register;